import React, { createContext, useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { OrderViewApi, OrderCreateApi, OrderUpdateApi } from "../../Utility/UtilApi/UtilApi";
import UtilToastMessages from "../../Utility/UtilToastMessages/UtilToastMessages";

export const StoreContext = createContext();

export const StoreProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasExistingOrder, setHasExistingOrder] = useState(false);
  const [OrderRecordId, setOrderRecordId] = useState("");
  const [dealLineItems, setDealLineItems] = useState([]);
  const [hasExistingWishList, setHasExistingWishList] = useState(false);
  const [wishListRecordId, setWishListRecordId] = useState("");
  const [wishListItems, setWishListItems] = useState([]);
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastColor, setToastColor] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currencyCode, setCurrencyCode] = useState("");
  const [localCartItems, setLocalCartItems] = useState(() => {
    return JSON.parse(localStorage.getItem("cart")) || [];
  });
  const [localWishlist, setLocalWishlist] = useState(() => {
    return JSON.parse(localStorage.getItem("wishlist")) || [];
  });
  const [cartCount, setCartCount] = useState(0);

  // Check login status from cookies
  useEffect(() => {
    const roleFromCookie = Cookies.get("lkmhbdsfsxasdweioisndi");
    const loggedIn = !!roleFromCookie;
    setIsLoggedIn(loggedIn);
    console.log("Initial isLoggedIn:", loggedIn);
  }, []);

  // Sync local cart and wishlist when user logs in
  useEffect(() => {
    if (isLoggedIn) {
      syncLocalItemsToServer();
    }
  }, [isLoggedIn]);

  // Update cart count when dependencies change
  useEffect(() => {
    getAndUpdateCartCount();
  }, [isLoggedIn, hasExistingOrder, dealLineItems, localCartItems]);

  const fetchCurrentDeals = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await OrderViewApi();
      if (response.data && response.data.orders.length > 0) {
        setCurrencyCode(response?.data?.currencycode);
        const orderDeal = response.data.orders.find((deal) => deal.type === "Order");
        if (orderDeal) {
          setHasExistingOrder(true);
          setOrderRecordId(orderDeal.recordid);
          setDealLineItems(orderDeal.deallineitems || []);
        } else {
          setHasExistingOrder(false);
          setOrderRecordId("");
          setDealLineItems([]);
        }

        const wishListDeal = response.data.orders.find((deal) => deal.type === "Wish List");
        if (wishListDeal) {
          setHasExistingWishList(true);
          setWishListRecordId(wishListDeal.recordid);
          setWishListItems(wishListDeal.deallineitems || []);
        } else {
          setHasExistingWishList(false);
          setWishListRecordId("");
          setWishListItems([]);
        }
      } else {
        setHasExistingOrder(false);
        setOrderRecordId("");
        setDealLineItems([]);
        setHasExistingWishList(false);
        setWishListRecordId("");
        setWishListItems([]);
      }
    } catch (error) {
      console.error("Error fetching deal data:", error);
      setIsLoggedIn(false);
    } finally {
      setLoading(false);
    }
  };

  const createDeal = async (type, items) => {
    const generateName = () => `${type}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const payload = {
      order: {
        name: generateName(),
        type: type,
      },
      deallineitems: items.map((item) => ({
        name: item.name,
        product: item.id,
        quantity: item.quantity || 1,
        unitpriceexclvat: item.unitpriceexclvat || 0,
        vat: item.vat || 0,
        discount: item.discount || 0,
      })),
    };

    const response = await OrderCreateApi(payload);
    if (response.success) {
      setToastMessage(`${type} created and items synced!`);
      setToastColor("success");
      setSnackbarOpen(true);
    } else {
      throw new Error(response.message || `Failed to create ${type}`);
    }
    return response;
  };

  const updateDeal = async (type, recordId, existingItems, newItems) => {
    let updatedLineItems = existingItems.map((item) => ({
      recordid: item.recordid,
      name: item.name,
      product: item.product.id || item.product,
      quantity: item.quantity,
      unitpriceexclvat: item.unitpriceexclvat,
      vat: item.vat,
      discount: item.discount || 0,
    }));

    newItems.forEach((newItem) => {
      const existingIndex = updatedLineItems.findIndex((item) => item.product === newItem.id);
      if (existingIndex !== -1) {
        updatedLineItems[existingIndex].quantity += newItem.quantity || 1;
      } else {
        updatedLineItems.push({
          name: newItem.name,
          product: newItem.id,
          quantity: newItem.quantity || 1,
          unitpriceexclvat: newItem.unitpriceexclvat || 0,
          vat: newItem.vat || 0,
          discount: newItem.discount || 0,
        });
      }
    });

    const payload = {
      order: {
        recordid: recordId,
        type: type,
        stage: "In Progress",
      },
      deallineitems: updatedLineItems,
    };

    const response = await OrderUpdateApi(payload);
    if (response.success) {
      setToastMessage(`Items synced to ${type}!`);
      setToastColor("success");
      setSnackbarOpen(true);
    } else {
      throw new Error(response.message || `Failed to update ${type}`);
    }
    return response;
  };

  const syncLocalItemsToServer = async () => {
    if (!isLoggedIn) return;
  
    try {
      // Fetch the latest server state
      const response = await OrderViewApi();
      console.log("OrderViewApi response:", response); // Debug log
  
      // Process the response to determine order and wishlist status
      let orderExists = false;
      let orderRecordId = "";
      let orderLineItems = [];
      let wishListExists = false;
      let wishListRecordId = "";
      let wishListItems = [];
  
      if (response.data && response.data.orders && response.data.orders.length > 0) {
        setCurrencyCode(response?.data?.currencycode || "");
  
        const orderDeal = response.data.orders.find((deal) => deal.type === "Order");
        if (orderDeal) {
          orderExists = true;
          orderRecordId = orderDeal.recordid;
          orderLineItems = orderDeal.deallineitems || [];
        }
  
        const wishListDeal = response.data.orders.find((deal) => deal.type === "Wish List");
        if (wishListDeal) {
          wishListExists = true;
          wishListRecordId = wishListDeal.recordid;
          wishListItems = wishListDeal.deallineitems || [];
        }
      }
  
      // Update state after processing response
      setHasExistingOrder(orderExists);
      setOrderRecordId(orderRecordId);
      setDealLineItems(orderLineItems);
      setHasExistingWishList(wishListExists);
      setWishListRecordId(wishListRecordId);
      setWishListItems(wishListItems);
  
      console.log("Order status - exists:", orderExists, "recordId:", orderRecordId); // Debug log
  
      // Sync Cart
      if (localCartItems.length > 0) {
        if (!orderExists) {
          console.log("Creating new order with items:", localCartItems); // Debug log
          await createDeal("Order", localCartItems);
        } else {
          console.log("Updating existing order:", orderRecordId, "with items:", localCartItems); // Debug log
          await updateDeal("Order", orderRecordId, orderLineItems, localCartItems);
        }
        setLocalCartItems([]); // Clear local cart
        localStorage.removeItem("cart");
      }
  
      // Sync Wishlist
      if (localWishlist.length > 0) {
        if (!wishListExists) {
          console.log("Creating new wishlist with items:", localWishlist); // Debug log
          await createDeal("Wish List", localWishlist);
        } else {
          console.log("Updating existing wishlist:", wishListRecordId, "with items:", localWishlist); // Debug log
          await updateDeal("Wish List", wishListRecordId, wishListItems, localWishlist);
        }
        setLocalWishlist([]); // Clear local wishlist
        localStorage.removeItem("wishlist");
      }
  
      // Refresh server state after sync
      await fetchCurrentDeals();
    } catch (error) {
      console.error("Error syncing local items to server:", error);
      setToastMessage("Failed to sync local items to server.");
      setToastColor("error");
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    fetchCurrentDeals();
  }, [isLoggedIn]);

  const updateDealState = (dealData) => {
    if (dealData) {
      if (dealData.type === "Order") {
        setHasExistingOrder(true);
        setOrderRecordId(dealData.recordid);
        setDealLineItems(dealData.deallineitems || []);
      } else if (dealData.type === "Wish List") {
        setHasExistingWishList(true);
        setWishListRecordId(dealData.recordid);
        setWishListItems(dealData.deallineitems || []);
      }
    }
  };

  const updateLocalCart = (newCartItems) => {
    setLocalCartItems(newCartItems);
    localStorage.setItem("cart", JSON.stringify(newCartItems));
    getAndUpdateCartCount();
  };

  const updateLocalWishlist = (newWishlistItems) => {
    setLocalWishlist(newWishlistItems);
    localStorage.setItem("wishlist", JSON.stringify(newWishlistItems));
  };

  const getAndUpdateCartCount = () => {
    let count = 0;
    if (isLoggedIn && hasExistingOrder) {
      // Authenticated: Sum quantities from dealLineItems
      count = dealLineItems.reduce((total, item) => total + (item.quantity || 0), 0);
    } else {
      // Unauthenticated: Sum quantities from localCartItems
      count = localCartItems.reduce((total, item) => total + (item.quantity || 0), 0);
    }
    setCartCount(count);
    return count;
  };
  return (
    <>
      <StoreContext.Provider
        value={{
          isLoggedIn,
          setIsLoggedIn,
          hasExistingOrder,
          OrderRecordId,
          dealLineItems,
          hasExistingWishList,
          wishListRecordId,
          wishListItems,
          loading,
          error,
          fetchCurrentDeals,
          updateDealState,
          currencyCode,
          localCartItems,
          updateLocalCart,
          localWishlist,
          updateLocalWishlist,
          cartCount,
          getAndUpdateCartCount,
          syncLocalItemsToServer,
        }}
      >
        {children}
      </StoreContext.Provider>

      <UtilToastMessages
        isOpen={isSnackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        data={toastMessage}
        severity={toastColor}
      />
    </>
  );
};

export const useStore = () => useContext(StoreContext);